import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  ChevronDown,
  ChevronRight,
  BookOpen,
  UserCog,
  Bell,
  Mail,
  Building2,
  SlidersHorizontal,
  Briefcase,
  Clock,
  ArrowLeft,
  UserCircle,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { NotificationBell } from './notifications/NotificationBell'

interface LayoutProps {
  children: React.ReactNode
}

interface NavItem {
  name: string
  href?: string
  icon: React.ComponentType<{ className?: string }>
  adminOnly?: boolean
  employeeOnly?: boolean
  children?: { name: string; href: string; icon: React.ComponentType<{ className?: string }> }[]
}

const navigation: NavItem[] = [
  { name: 'Pantalla principal', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Empleados', href: '/employees', icon: Users, adminOnly: true },
  { name: 'Solicitudes', href: '/requests', icon: FileText },
  { name: 'Notificaciones', href: '/notifications', icon: Bell, employeeOnly: true },
  { name: 'Manual de Usuario', href: '/manual', icon: BookOpen, employeeOnly: true },
  {
    name: 'Configuración',
    icon: Settings,
    adminOnly: true,
    children: [
      { name: 'Reglas', href: '/configuration/rules', icon: BookOpen },
      { name: 'Límites', href: '/configuration/limits', icon: SlidersHorizontal },
      { name: 'Centros de Trabajo', href: '/configuration/work-centers', icon: Building2 },
      { name: 'Turnos', href: '/configuration/shifts', icon: Clock },
      { name: 'Puestos', href: '/configuration/positions', icon: Briefcase },
      { name: 'Administradores', href: '/configuration/users', icon: UserCog },
      { name: 'Emails notificacion', href: '/configuration/emails', icon: Mail },
      { name: 'Manuales de Usuario', href: '/configuration/manuals', icon: FileText },
    ],
  },
]

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const { toggleTheme, isDark } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(() => {
    // Auto-expand configuration if on a configuration page
    if (location.pathname.startsWith('/configuration')) {
      return new Set(['Configuración'])
    }
    return new Set()
  })

  // Estado para impersonate
  const [impersonating, setImpersonating] = useState<string | null>(null)

  useEffect(() => {
    // Verificar si estamos en modo impersonate
    const impersonatingName = localStorage.getItem('impersonating')
    setImpersonating(impersonatingName)
  }, [])

  const handleBackToAdmin = () => {
    // Restaurar el token y usuario del admin
    const adminToken = localStorage.getItem('adminToken')
    const adminUser = localStorage.getItem('adminUser')

    if (adminToken && adminUser) {
      localStorage.setItem('token', adminToken)
      localStorage.setItem('user', adminUser)

      // Limpiar datos de impersonate
      localStorage.removeItem('adminToken')
      localStorage.removeItem('adminUser')
      localStorage.removeItem('impersonating')

      // Recargar para aplicar cambios
      window.location.href = '/employees'
    }
  }

  const handleLogout = () => {
    // Limpiar también datos de impersonate si existen
    localStorage.removeItem('adminToken')
    localStorage.removeItem('adminUser')
    localStorage.removeItem('impersonating')
    logout()
    navigate('/login')
  }

  const toggleMenu = (menuName: string) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(menuName)) {
        newSet.delete(menuName)
      } else {
        newSet.add(menuName)
      }
      return newSet
    })
  }

  const filteredNav = navigation.filter((item) => {
    // Si es solo para admin, verificar que sea admin
    if (item.adminOnly && user?.role !== 'ADMIN') return false
    // Si es solo para empleados, verificar que sea empleado
    if (item.employeeOnly && user?.role !== 'EMPLOYEE') return false
    return true
  })

  const renderNavItem = (item: NavItem, onItemClick?: () => void) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedMenus.has(item.name)
    const isActive = item.href
      ? location.pathname.startsWith(item.href)
      : item.children?.some((child) => location.pathname === child.href)

    if (hasChildren) {
      return (
        <div key={item.name}>
          <button
            onClick={() => toggleMenu(item.name)}
            className={`sidebar-item w-full justify-between ${isActive ? 'sidebar-item-active' : ''}`}
          >
            <div className="flex items-center">
              <item.icon
                className={`mr-3 h-5 w-5 ${
                  isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              />
              {item.name}
            </div>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )}
          </button>
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children?.map((child) => {
                const childActive = location.pathname === child.href
                return (
                  <Link
                    key={child.href}
                    to={child.href}
                    className={`sidebar-item pl-6 ${childActive ? 'sidebar-item-active' : ''}`}
                    onClick={onItemClick}
                  >
                    <child.icon
                      className={`mr-3 h-4 w-4 ${
                        childActive
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-gray-400 dark:text-gray-500'
                      }`}
                    />
                    {child.name}
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      )
    }

    return (
      <Link
        key={item.name}
        to={item.href!}
        className={`sidebar-item ${isActive ? 'sidebar-item-active' : ''}`}
        onClick={onItemClick}
      >
        <item.icon
          className={`mr-3 h-5 w-5 ${
            isActive
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-gray-400 dark:text-gray-500'
          }`}
        />
        {item.name}
      </Link>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}
      >
        <div
          className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-[85vw] max-w-[300px] flex-col sidebar animate-slide-in">
          <div className="flex h-16 sm:h-20 items-center justify-between px-3 sm:px-4 border-b border-gray-200 dark:border-dark-border">
            <div className="flex items-center gap-3">
              <img
                src="/logo/ES_Viscofan_50_Logo.png"
                alt="Viscofan"
                className="h-10 sm:h-12 w-auto"
              />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 dark:text-gray-400"
              aria-label="Cerrar menú"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 sm:px-3 py-3 sm:py-4 overflow-y-auto scrollbar-thin">
            {filteredNav.map((item) => renderNavItem(item, () => setSidebarOpen(false)))}
          </nav>
          <div className="border-t border-gray-200 dark:border-dark-border p-3 sm:p-4 safe-area-inset-bottom">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold text-sm sm:text-base flex-shrink-0">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            {/* Botones de tema y logout para móvil */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  toggleTheme()
                }}
                className="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-700 transition-colors border border-gray-200 dark:border-dark-border"
              >
                {isDark ? (
                  <>
                    <Sun className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>Claro</span>
                  </>
                ) : (
                  <>
                    <Moon className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>Oscuro</span>
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setSidebarOpen(false)
                  handleLogout()
                }}
                className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-lg bg-error-50 text-error-600 hover:bg-error-100 dark:bg-error-900/20 dark:text-error-400 dark:hover:bg-error-900/30 transition-colors"
                title="Cerrar sesión"
              >
                <LogOut className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>Salir</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-grow sidebar">
          <div className="flex h-24 items-center px-6 border-b border-gray-200 dark:border-dark-border">
            <img
              src="/logo/ES_Viscofan_50_Logo.png"
              alt="Viscofan"
              className="h-16 w-auto"
            />
          </div>
          <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto scrollbar-thin">
            {filteredNav.map((item) => renderNavItem(item))}
          </nav>
          <div className="border-t border-gray-200 dark:border-dark-border p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-700 transition-colors"
                title={isDark ? 'Modo claro' : 'Modo oscuro'}
              >
                {isDark ? (
                  <>
                    <Sun className="h-4 w-4" />
                    <span>Claro</span>
                  </>
                ) : (
                  <>
                    <Moon className="h-4 w-4" />
                    <span>Oscuro</span>
                  </>
                )}
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-100 hover:text-error-600 dark:text-gray-300 dark:hover:bg-slate-700 dark:hover:text-error-400 transition-colors"
                title="Cerrar sesión"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72 flex flex-col min-h-screen">
        {/* Mobile top bar - fixed position */}
        <div className="sticky top-0 z-40 flex h-14 sm:h-16 items-center gap-x-2 sm:gap-x-4 border-b border-gray-200 dark:border-dark-border bg-white/95 dark:bg-dark-card/95 backdrop-blur-xl px-3 sm:px-4 shadow-sm lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-1 text-gray-500 hover:bg-gray-100 rounded-lg dark:text-gray-400 dark:hover:bg-slate-700 flex-shrink-0"
            aria-label="Abrir menú"
          >
            <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          <div className="flex-1 flex justify-center min-w-0">
            <img
              src="/logo/ES_Viscofan_50_Logo.png"
              alt="Viscofan"
              className="h-8 sm:h-10 w-auto"
            />
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <NotificationBell />
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg dark:text-gray-400 dark:hover:bg-slate-700"
              aria-label={isDark ? 'Activar modo claro' : 'Activar modo oscuro'}
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Desktop top bar */}
        <div className="hidden lg:flex sticky top-0 z-40 h-16 items-center justify-end gap-x-4 border-b border-gray-200 dark:border-dark-border bg-white/80 dark:bg-dark-card/80 backdrop-blur-xl px-8 shadow-sm">
          <NotificationBell />
        </div>

        {/* Banner de impersonate */}
        {impersonating && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 sm:px-4 py-2 sm:py-3 shadow-lg">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <UserCircle className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                <span className="font-medium text-sm sm:text-base">
                  <span className="hidden sm:inline">Estás viendo la aplicación como: </span>
                  <span className="sm:hidden">Como: </span>
                  <strong className="truncate">{impersonating}</strong>
                </span>
              </div>
              <button
                onClick={handleBackToAdmin}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors text-sm flex-shrink-0"
              >
                <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Volver a Admin</span>
                <span className="sm:hidden">Volver</span>
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 p-3 sm:p-4 lg:p-8 pb-6">{children}</main>
      </div>
    </div>
  )
}
